import { NotFound } from '@components/404'
import { Layout } from '@components/common'
import { ProductItem } from '@components/common/ProductTile/ProductTile'
import { ProductInfo } from '@components/corra/ProductInfo'
import { RelatedProducts } from '@components/corra/RelatedProducts'
import { FrequentlyBoughtTogetherProducts } from '@components/corra/FrequentlyBoughtTogetherProducts'
import { Breadcrumbs } from '@corratech/pylot-breadcrumbs'
import {
    GalleryTypeEnum,
    ProductGallery
} from '@corratech/pylot-product-gallery'
import { GiftCardProduct } from '@corratech/pylot-product-giftcard'
import { RichContent } from '@corratech/pylot-rich-content'
import { SEO, PAGE_TYPE } from '@corratech/pylot-seo'
import { useDataLayerAction } from '@corratech/pylot-tag-manager'
import { Accordion, Container } from '@corratech/pylot-ui'
import { useUI } from '@corratech/pylot-ui/context'
import { DesignProductAttributes } from '@components/DesignProductAttributes/DesignProductAttributes'
import s from '@pagestyles/PDP.module.scss'
import getProduct from '@pylot-data/api/operations/get-product'
import { graphqlFetch } from '@pylot-data/graphqlFetch'
import { useCmsBlocks } from '@pylot-data/hooks/cms/use-cms-blocks'
import { useProductFetch } from '@pylot-data/hooks/product/use-product-fetch'
import { useProductUI } from '@pylot-data/hooks/product/use-product-ui'
import {
    CmsBlock,
    ComplexTextValue,
    ConfigurableProduct,
    Maybe,
    SimpleProduct,
    StoreStockInput
} from '@pylot-data/pylotschema'
import cn from 'classnames'
import { getStoreConfig } from 'config'
import type {
    GetStaticPaths,
    GetStaticProps,
    GetStaticPropsContext,
    InferGetStaticPropsType
} from 'next'
import { TFunction, useTranslation } from 'next-i18next/pages'
import { serverSideTranslations } from 'next-i18next/pages/serverSideTranslations'
import { useRouter } from 'next/router'
import React, { ReactElement, useEffect, useRef, useState } from 'react'
import { IconClose, IconOpen } from '@components/icons'
import { viewItemEvent } from '@lib/utils/exponeaTrackingEvents'
import { productCustomAttributes } from '@pylot-data/constants/productCustomAttributes'
import { ProductItemMobile } from '@components/common/ProductTileMobile/ProductTileMobile'
import dynamic from 'next/dynamic'
import { isMobileScreen } from '@lib/utils/mediaQuery'
import { shouldPrerenderPaths } from '@lib/utils/staticPaths'
import { Select } from '@components/common/Select'
import { regionComunas } from '@pylot-data/constants/regionComunas'
// import { formatCurrency } from '@lib/utils/formatCurrency'
const Modal = dynamic(() => import('@corratech/pylot-ui/src/Modal'))
const NEW_FLAG_STRING = 'NUEVO'
const renderProductDescription = (
    t: TFunction,
    short_description: Maybe<ComplexTextValue>,
    firstAccordionBlockContent: Maybe<Maybe<CmsBlock | null>[]> | undefined,
    secondAccordionBlockContent: Maybe<Maybe<CmsBlock | null>[]> | undefined,
    product: ConfigurableProduct
) => {
    const tabs = [
        {
            id: 'short-description',
            Content: function AccordionContent() {
                return (
                    <>
                        <div className={s['short-description']}>
                            <RichContent html={short_description?.html} />
                        </div>
                        {Object.entries(product).map(([key, value]) => {
                            const isCustomAttributeDisplayed =
                                productCustomAttributes[key] &&
                                value !== null &&
                                value !== ''
                            if (isCustomAttributeDisplayed) {
                                return (
                                    <div key={key} className="mb-5 md:mb-6">
                                        <h6 className="mb-2 font-tertiary font-medium text-sm md:text-base leading-extra-4.4 md:leading-5">
                                            {productCustomAttributes[key]}
                                        </h6>
                                        <div className="font-primary font-normal text-sm md:text-base leading-5 md:leading-extra-1.6">
                                            <RichContent
                                                html={value as string}
                                            />
                                        </div>
                                    </div>
                                )
                            }
                            return null
                        })}
                    </>
                )
            },
            buttonClass: ({ isOpen }: { isOpen: boolean }) =>
                cn(
                    {
                        'mb-6 md:mb-9': isOpen
                    },
                    'py-5'
                ),
            Title: function AccordionTitle() {
                return (
                    <h5 className="uppercase font-bold text-base md:text-xl leading-5 md:leading-6">
                        {t('condiciones de uso')}
                    </h5>
                )
            },
            initialState: true,
            itemClassName: 'border-b border-color-s-4'
        },
        {
            id: '2',
            Title: function AccordionTitle() {
                return (
                    <h5 className={cn('uppercase text-base md:text-xl mr-2')}>
                        {t('Venta corporativa')}
                    </h5>
                )
            },
            Content: function AccordionContent() {
                return (
                    <div className="flex flex-col gap-y-4">
                        <RichContent
                            html={firstAccordionBlockContent?.[0]?.content}
                        />
                    </div>
                )
            },
            buttonClass: ({ isOpen }: { isOpen: boolean }) =>
                cn(
                    {
                        'mb-6 md:mb-9': isOpen
                    },
                    'py-5'
                ),
            buttonStyles: () => ({
                justifyContent: 'space-between'
            }),
            itemClassName: 'border-b border-color-s-4'
        }
    ]
    return tabs
}
const FIRST_ACCORDION_PREFIX = 'accordion-venta-corporativa'
const SECOND_ACCORDION_PREFIX = 'accordion-cambios-devoluciones'
export const getStaticPaths: GetStaticPaths = async () => {
    if (!shouldPrerenderPaths()) {
        return { paths: [], fallback: 'blocking' }
    }

    // TODO: [PYLOT-247] Preload pages for non-English locales
    try {
        const config = getStoreConfig() // Pass locale as an argument
        const productSearch = await graphqlFetch<
            Record<string, never>,
            { products: { items: ConfigurableProduct[] } }
        >({
            query: /* GraphQL */ `
        {
            products(
                pageSize: ${config.static.products.count}
                currentPage: 1
                search: ""
            ) {
                items {
                    url_key
                }
            }
        }
    `
        })
        const items = productSearch.data.products.items.filter(
            (item) => item.url_key !== ''
        )
        const paths = items.map((product) => ({
            params: { detail_url: product.url_key! },
            locale: 'es'
        }))
        return {
            // automatically generate paths for all the products
            paths,
            fallback: 'blocking'
        }
    } catch (e) {
        console.log('PDP GETSTATICPATHS ERROR')
        console.log(e)
        return {
            paths: [],
            fallback: 'blocking'
        }
    }
}
// const returnPrice = (product: any) => {
//     if (product?.giftcard_amounts) {
//         return product?.giftcard_amounts[0].value
//     }
//     return 0
// }
export const getStaticProps: GetStaticProps = async ({
    locale,
    params
}: GetStaticPropsContext) => {
    // category adds .html postfix
    const productUrlWithPostfix = params!.detail_url as string
    // TODO: remove that
    try {
        const productUrl = productUrlWithPostfix.replace('.html', '')
        const product = await getProduct(productUrl, locale || '')
        if (!product || !product?.productDetail?.items.length) {
            return {
                notFound: true
            }
        }
        return {
            props: {
                url_key: productUrl,
                productData: product,
                ...(await serverSideTranslations(locale!, [
                    'common',
                    'pdp',
                    'wishlist'
                ]))
            },
            revalidate: 14400
        }
    } catch (e) {
        console.log('PDP GETSTATICPROPS ERROR')
        console.log(e)
        return {
            props: {
                ...(await serverSideTranslations(locale!, [
                    'common',
                    'wishlist'
                ]))
            },
            revalidate: 14400
        }
    }
}
const WrappedProductItem = ({
    product
}: {
    product: SimpleProduct | ConfigurableProduct
}): JSX.Element => {
    return (
        <div key={product.id}>
            <ProductItem product={product} isPDP imageParam={{ width: 500 }} />
        </div>
    )
}
const WrappedFBTProductItem = ({
    product
}: {
    product: SimpleProduct | ConfigurableProduct
}): JSX.Element => {
    const isMobile = isMobileScreen()
    return isMobile ? (
        <div key={product.id}>
            <ProductItemMobile product={product} imageParam={{ width: 100 }} />
        </div>
    ) : (
        <div>
            <ProductItem product={product} isPDP imageParam={{ width: 500 }} />
        </div>
    )
}
export default function GiftCardDetail({
    url_key,
    productData
}: InferGetStaticPropsType<typeof getStaticProps>): ReactElement {
    const [displayStoreStock, setDisplayStoreStock] = useState(false)
    const [storeStockItems, setStoreStockItems] = useState([])
    const [emptyStoreStock, setEmptyStoreStock] = useState(false)
    const [defaultStoreRegion, setDefaultStoreRegion] = useState('default')
    const router = useRouter()
    const { t } = useTranslation(['common', 'pdp', 'wishlist'])
    const isMobile = isMobileScreen()
    const { LoadingIndicator } = useUI()
    const dataLayerActionRef = useRef(true)
    const dataLayerAction = useDataLayerAction()
    const config = getStoreConfig()
    // Note to project teams - you can remove the `isStackedGallery` URL parameter option
    // by hardcoding isStacked either true or false.
    // This is only used for demo purposes in Pylot.
    const { isStackedGallery } = router.query
    const isStacked = isStackedGallery !== 'false'
    const { product, isSupportedProductType, error, isValidating } =
        useProductFetch(
            {
                productUrl:
                    url_key ||
                    router.asPath
                        .split('/giftcard/detail/')?.[1]
                        .replace('.html', '')
            },
            {
                revalidateOnFocus: false,
                revalidateOnMount: true,
                initialData: { data: productData }
            }
        )
    const productFromProps = productData?.productDetail?.items?.[0]
    const resolvedProduct = product ?? productFromProps

    const { items: firstAccordionBlockContent } = useCmsBlocks(
        [FIRST_ACCORDION_PREFIX || ''],
        {
            revalidateOnMount: true,
            revalidateOnFocus: false
        }
    )
    const { items: secondAccordionBlockContent } = useCmsBlocks(
        [SECOND_ACCORDION_PREFIX || ''],
        {
            revalidateOnMount: true,
            revalidateOnFocus: false
        }
    )
    const { variant, mediaGalleryEntries, selectedOptionUIDs } =
        useProductUI(resolvedProduct)
    useEffect(() => {
        if (!(product && dataLayerActionRef.current)) return
        const finalPriceInformation =
            product?.price_range?.minimum_price?.final_price
        const categoryName = sessionStorage?.getItem('origin-category')
        dataLayerAction({
            type: 'PDP',
            data: {
                type: 'ProductDetailsImpressions',
                id: variant.sku,
                name: product.name,
                categories: product.categories,
                price: finalPriceInformation?.value,
                currencyCode: finalPriceInformation?.currency,
                category: { name: categoryName }
            }
        })
        dataLayerActionRef.current = false
    }, [dataLayerAction, product])
    // this effect is necessary to target the global help iframe and move it, because it blocks the mobile fixed action bar.
    useEffect(() => {
        const addClassToElement = () => {
            const iframeElement = document.getElementById('launcher')
            if (iframeElement && isMobile) {
                iframeElement.classList.add(s.launcherPdp)
                observer.disconnect() // Stop observing once the element is found
            }
        }
        const observer = new MutationObserver(addClassToElement)
        observer.observe(document.body, { childList: true, subtree: true })
        return () => {
            observer.disconnect()
        }
    }, [isMobile])
    useEffect(() => {
        viewItemEvent(product)
    }, [url_key])
    useEffect(() => {
        if (variant?.sku && variant.sku !== product.sku) {
            const viewedProd = Object.assign({}, product)
            viewedProd.id = variant.id
            viewedProd.sku = variant.sku
            viewedProd.price_range = variant.price_range
            viewItemEvent(viewedProd)
        }
    }, [variant?.sku])
    const isPageLoading =
        !resolvedProduct && (isValidating || router.isFallback)

    if (isPageLoading) return <LoadingIndicator />
    if (!resolvedProduct && error) {
        return (
            <Container clean className={s['container-classes']}>
                <h1>{t('Error!!!')}</h1>
            </Container>
        )
    }
    if (!resolvedProduct) {
        return <NotFound identifier={config.base.route?.cms_no_route!} />
    }
    //Warning if the product type is not supported
    if (product && !isSupportedProductType) {
        return (
            <Container clean className={s['container-classes']}>
                <h1>{`${product?.__typename} ${t('is not supported')}`}</h1>
            </Container>
        )
    }
    const activeProduct = resolvedProduct

    const {
        name,
        short_description = { html: '' },
        description,
        price_range: productPriceRange,
        badge_image_text: badgeImageText,
        flag
    } = activeProduct
    const {
        sku = activeProduct.sku,
        price_range: variantPriceRange = activeProduct.price_range
    } = variant ?? {}
    let flagNuevo
    activeProduct.categories?.forEach((categoria = Object) => {
        if (categoria.name === 'Nuevos') {
            flagNuevo = 1
        }
    })
    const badgeLabel = flagNuevo ? NEW_FLAG_STRING : badgeImageText
    const badgeText = flag ? flag.label : badgeLabel
    // Workaround to show a custom label for FBT products
    activeProduct.customTextAfterProductImage = t('pdp||This product')
    const DEFAULT = 'default'
    const disenoAttr = activeProduct && activeProduct?.diseno_para_ti
    const attrGroup = disenoAttr
        ? disenoAttr.split(',').map((item: string) => item.trim())
        : []
    const handleStock = async (value: string) => {
        const skuNumber: string = sku ? sku : ''
        setDefaultStoreRegion(value)
        const regionValue = value.length <= 1 ? `0${value}` : value
        setEmptyStoreStock(false)
        const response = await graphqlFetch<StoreStockInput, any>({
            query: `
            query storeStock($input: StoreStockInput!) {
                storeStock(input: $input) {
                    localId
                    nombreLocal
                    region
                    ciudad
                    comuna
                    codBodega
                    nombreBodega
                    sku
                    stock
                }
            }
            `,
            variables: {
                input: {
                    regionId: regionValue,
                    sku: skuNumber
                }
            },
            customPylotQueryHeader: 'config'
        })
        if (response?.data?.storeStock) {
            setStoreStockItems(response?.data?.storeStock)
            setEmptyStoreStock(response?.data?.storeStock.length === 0)
        }
    }
    return (
        <Container clean>
            {displayStoreStock ? (
                <div className={s['store-stock-modal-container']}>
                    <Modal
                        onClose={() => setDisplayStoreStock(false)}
                        open={displayStoreStock}
                        closeOnOverlayClick
                    >
                        <div className={s['store-stock-modal-content']}>
                            <p className={s['store-stock-modal-title']}>
                                {t('Stock en tiendas')}
                            </p>
                            <div className="py-1">
                                <label className="heading heading-no-capitalize">
                                    {t('auth|Region')}
                                    <span className="asterisk">*</span>
                                </label>
                                <Select
                                    id="select-region"
                                    defaultValue={defaultStoreRegion}
                                    value={DEFAULT}
                                    onChange={handleStock}
                                >
                                    <option
                                        value={DEFAULT}
                                        selected
                                        disabled
                                        hidden
                                    >
                                        {t('auth|Please Select the Region')}
                                    </option>
                                    {regionComunas.map((region) => (
                                        <option
                                            key={region.id}
                                            value={region.id}
                                        >
                                            {region.name}
                                        </option>
                                    ))}
                                </Select>
                            </div>
                            <div>
                                {storeStockItems.length ? (
                                    <>
                                        <p
                                            className={
                                                s['store-stock-modal-title']
                                            }
                                        >
                                            {t('Disponibilidad en tienda')}
                                        </p>
                                        <div
                                            className={
                                                s['store-stock-modal-list']
                                            }
                                        >
                                            {storeStockItems.map(
                                                (item: any, index) => (
                                                    <div
                                                        key={index}
                                                        className={
                                                            s[
                                                                'store-stock-item'
                                                            ]
                                                        }
                                                    >
                                                        <p>
                                                            {item.nombreLocal}
                                                        </p>
                                                        <p>
                                                            {item.stock > 0
                                                                ? `${item.stock} UN.`
                                                                : t(
                                                                      'no hay stock'
                                                                  )}
                                                        </p>
                                                    </div>
                                                )
                                            )}
                                        </div>
                                        <div className="md:m-8 md:mb-0 m-4 mb-0 text-sm">
                                            {t(
                                                '• La disponibilidad de productos está dispuesta a cambios, puede modificarse durante el día'
                                            )}
                                        </div>
                                    </>
                                ) : null}
                                {emptyStoreStock && (
                                    <div>
                                        {t(
                                            'No hay stock disponible en ninguna tienda de esta región.'
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </Modal>
                </div>
            ) : null}
            <div className={s['container-classes']}>
                <SEO product={activeProduct} page={PAGE_TYPE.PDP} />
                <div className={cn(s['pdp-breadcrumbs'], 'breadcrumbs')}>
                    {activeProduct.categories?.length ? (
                        <Breadcrumbs
                            product={activeProduct}
                            separator="|"
                            // eslint-disable-next-line i18next/no-literal-string
                            urlPrefix="/categoria"
                            rootText={t('breadcrumbs|Home')}
                        />
                    ) : null}
                </div>
                <div className={s['pdp-container']}>
                    <ProductGallery
                        mediaGalleryEntries={mediaGalleryEntries}
                        config={{
                            mobile: {
                                gallerytype: GalleryTypeEnum.SCROLL,
                                zoom: false,
                                fullscreen: false,
                                badgeLabel: badgeText
                            },
                            desktop: {
                                gallerytype: isStacked
                                    ? GalleryTypeEnum.STACKED
                                    : GalleryTypeEnum.SCROLL,
                                zoom: false,
                                fullscreen: true,
                                thumb: true,
                                enableLastVideoAsFullWidth: true,
                                imagesPerPage: 4,
                                loadMoreLabel: t('pdp||Load More'),
                                badgeLabel: badgeText
                            }
                        }}
                    />
                    <div className={s['product-main']}>
                        <div className={s['product-details']}>
                            <div className={s['product-topinfo']}>
                                <span className={s['sku']} aria-label={sku}>
                                    {t('SKU: ')}
                                    {sku}
                                </span>
                            </div>

                            <div>
                                <h1
                                    className={cn(
                                        s['product-name'],
                                        'heading-4'
                                    )}
                                >
                                    {name}
                                </h1>
                            </div>
                            <ProductInfo description={description} />
                            {/* <div className={cn(s['product-name'], 'heading-4')}>
                                {formatCurrency(returnPrice(product))}
                            </div> */}
                            <GiftCardProduct product={activeProduct} />
                            <Accordion
                                className={cn(
                                    s['giftcard-accordion'],
                                    'border-t border-color-s-4'
                                )}
                                OpenIcon={<IconOpen className="ml-auto mr-6" />}
                                CloseIcon={
                                    <IconClose className="ml-auto mr-6" />
                                }
                                contents={renderProductDescription(
                                    t,
                                    short_description,
                                    firstAccordionBlockContent,
                                    secondAccordionBlockContent,
                                    activeProduct
                                )}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {attrGroup.length > 0 && (
                <div className={s['container-classes']}>
                    <DesignProductAttributes disenado_para_ti={attrGroup} />
                </div>
            )}
            <div className={s['container-classes px-0']}>
                <FrequentlyBoughtTogetherProducts
                    currentProduct={activeProduct}
                    currentProductPricing={
                        variantPriceRange || productPriceRange
                    }
                    currentProductSelectedOptionsUIDs={selectedOptionUIDs}
                    products={activeProduct?.fbt_products}
                    ProductTile={WrappedFBTProductItem}
                />
            </div>
            <div className={s['container-classes']}>
                <div id="exponea-carousel-nuestros-disenadores-te-sugieren-pdp" />
                <div id="exponea-carousel-recientemente-vistos-pdp" />
                <RelatedProducts
                    products={activeProduct?.related_products}
                    ProductTile={WrappedProductItem}
                />
            </div>
        </Container>
    )
}
GiftCardDetail.Layout = Layout
